<template>
  <p-container>
    <h2 class="mb-4">
      {{ $t('core.page.contactidmapping.page.title') }}
    </h2>
    <p-request-alert v-model="requestError"></p-request-alert>
    <p-row>
      <p-col xs12 sm3>
        <p-text-field
          v-model="filter.searchCriteria"
          context-icon="search"
          clearable
          :label="$t('core.app.search')"
        ></p-text-field>
      </p-col>
      <p-col xs12 sm3>
        <p-select
          v-model="filter.foreignAppSystemIdCriteria"
          :loading="isLoadingTargetApps"
          context-icon="build"
          clearable
          :label="$t('core.page.contactidmapping.properties.foreignAppSystemId')"
          :items="appSystems"
        ></p-select>
      </p-col>
      <p-col xs12 sm3>
        <p-select
          v-model="filter.crmStatusIdCriteria"
          clearable
          :label="$t('core.page.contactidmapping.properties.crmStateName')"
          :items="crmStatus"
        ></p-select>
      </p-col>
      <p-col xs12 sm3>
        <p-select
          v-model="filter.mappingStatusIdCriteria"
          clearable
          :label="$t('core.page.contactidmapping.properties.mappingStateName')"
          :items="mappingStatus"
        ></p-select>
      </p-col>
    </p-row>
    <p-list-view ref="ordersList" v-model="list" :pagination.sync="pagination" :service-function="populateData" :columns="columns">
      <template slot="table-actions">
        <p-button @click="refresh">
          <p-icon>refresh</p-icon>
        </p-button>
      </template>
      <template #entry-actions="{entry}">
        <router-link :to="`/_admin/contactidmapping/${entry.id.value}`">
          <p-button>
            <p-icon>find_in_page</p-icon>
          </p-button>
        </router-link>
      </template>
    </p-list-view>
  </p-container>
</template>

<script lang="ts">
  import Vue from 'vue';
  import ResponsePagingInfo from '../../../../plugins/servicelayer/responseTypes/responsePagingInfo';
  import GetContactIdMappingsRequestViewModel from '../../../../services/v1/viewModel/request/ContactIdMappings/GetContactIdMappingsRequestViewModel';
  import AppSystemResourceViewModel from '../../../../services/v1/viewModel/resource/AppSystemResourceViewModel';

  export default Vue.extend({
    auth: true,
    role: ['admin', 'hostadmin'],
    layout: 'layout-hostadmin',
    data: () => ({
      pagination: {} as ResponsePagingInfo,
      isLoadingTargetApps: false,
      requestError: undefined as string | undefined,
      filter: {
        searchCriteria: undefined as string | undefined,
        foreignAppSystemIdCriteria: undefined as number | undefined,
        mappingStatusIdCriteria: undefined as number | undefined,
        crmStatusIdCriteria: undefined as number | undefined,
      },
      innerPagination: {
        'paging.CurrentPage': undefined as number | undefined,
        'paging.PageSize': undefined as number | undefined,
        'paging.IncludeCount': undefined as boolean | undefined,
        'paging.NoPaging': undefined as boolean | undefined,
      },
      list: {},
      columns: [] as {}[],
      appSystems: [] as AppSystemResourceViewModel[],
      mappingStatus: [] as {}[],
      crmStatus: [] as {}[],
    }),
    computed: {
      request() {
        return new GetContactIdMappingsRequestViewModel().fromModel({ ...this.filter, ...this.innerPagination });
      },
    },
    watch: {
      filter: {
        deep: true,
        handler() {
          this.pagination.currentPage = 1;
          this.innerPagination['paging.CurrentPage'] = 1;
          this.$debounce(() => {
            this.refresh();
          }, 600, this)();
        },
      },
      request() {
        this.writeRequestToQuery();
      },
    },
    beforeMount() {
      this.getQueryFromRequest();
      this.pagination.currentPage = this.innerPagination['paging.CurrentPage'] !;
      this.pagination.pageSize = this.innerPagination['paging.PageSize'] !;
    },
    mounted() {
      this.columns = [
        { value: '', field: 'filter' },
        { value: this.$t('core.page.contactidmapping.properties.id'), textAlign: 'left', field: 'id' },
        { value: this.$t('core.page.contactidmapping.properties.crmId'), textAlign: 'left', field: 'crmId' },
        { value: this.$t('core.page.contactidmapping.properties.crmStateName'), textAlign: 'left', field: 'crmStateName' },
        { value: this.$t('core.page.contactidmapping.properties.foreignAppSystemId'), textAlign: 'left', field: 'foreignAppSystemKey' },
        { value: this.$t('core.page.contactidmapping.properties.foreignId'), textAlign: 'left', field: 'foreignId' },
        { value: this.$t('core.page.contactidmapping.properties.mappingStateName'), textAlign: 'left', field: 'mappingStateName' },
        { value: this.$t('core.page.contactidmapping.properties.mappingStateReason'), textAlign: 'left', field: 'mappingStateReason' },
      ];
      this.mappingStatus = [
        { id: 0, caption: 'Active' },
        { id: 1, caption: 'Ignore' },
        { id: 2, caption: 'Error' },
      ];
      this.crmStatus = [
        { id: 0, caption: 'Unchanged' },
        { id: 1, caption: 'CrmInserted' },
        { id: 2, caption: 'CrmUpdated' },
        { id: 3, caption: 'CrmDeleted' },
      ];
      this.getAppSystems();
    },
    methods: {
      getQueryFromRequest() {
        const queryParams = new GetContactIdMappingsRequestViewModel();
        queryParams.fromQuery(this.$routerUtils.getQueryParams());
        this.filter.searchCriteria = queryParams.searchCriteria;
        this.filter.foreignAppSystemIdCriteria = queryParams.foreignAppSystemIdCriteria;
        this.filter.mappingStatusIdCriteria = queryParams.mappingStatusIdCriteria;
        this.filter.crmStatusIdCriteria = queryParams.crmStatusIdCriteria;

        this.innerPagination['paging.CurrentPage'] = queryParams['paging.CurrentPage'];
        this.innerPagination['paging.PageSize'] = queryParams['paging.PageSize'];
        this.innerPagination['paging.IncludeCount'] = queryParams['paging.IncludeCount'];
        this.innerPagination['paging.NoPaging'] = queryParams['paging.NoPaging'];
      },
      async writeRequestToQuery() {
        this.$debounce(() => {
          this.$routerUtils.updateQueryParams(this.request.toQuery(), { preserve: false });
        }, 200, this.writeRequestToQuery)();
      },
      refresh() {
        const ordersList = this.$refs.ordersList as any;
        ordersList?.populateList();
      },
      populateData(params: any) {
        this.innerPagination['paging.CurrentPage'] = params.query['paging.CurrentPage'];
        this.innerPagination['paging.PageSize'] = params.query['paging.PageSize'];
        this.innerPagination['paging.IncludeCount'] = params.query['paging.IncludeCount'];
        this.innerPagination['paging.NoPaging'] = params.query['paging.NoPaging'];
        return this.$service.api.core.contactIdMappings.getContactIdMappings(this.request);
      },
      async getAppSystems() {
        this.isLoadingTargetApps = true;
        try {
          const response = await this.$service.v1.api.core.appSystem.getAppSystem();
          this.appSystems = response.items;
        } catch (error: any) {
          this.requestError = error;
        }
        this.isLoadingTargetApps = false;
      },
    },
  });
</script>
